import React, { useState, useEffect } from 'react'
import { Container } from '@/components'
import { axios } from '@/lib/axios'
import {
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider,
} from '@mui/material'
import styled from 'styled-components'

const AdminContainer = styled.div`
  padding: 40px;
  max-width: 1200px;
  margin: 0 auto;
`

const FormContainer = styled(Paper)`
  padding: 30px;
  margin-bottom: 30px;
`

const FormField = styled.div`
  margin-bottom: 20px;
`

const AdminDashboard = styled.div`
  margin-bottom: 40px;
`

const AdminActionCard = styled(Card)`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

// Component for company and user creation
const CompanyUserCreationForm = ({ onBack }: { onBack: () => void }) => {
  // Company creation
  const [companyDomain, setCompanyDomain] = useState('')
  const [companyCreated, setCompanyCreated] = useState(false)
  const [createdCompanyId, setCreatedCompanyId] = useState('')
  const [companyLoading, setCompanyLoading] = useState(false)
  const [checkingDomain, setCheckingDomain] = useState(false)
  const [companyError, setCompanyError] = useState('')
  const [companyExists, setCompanyExists] = useState(false)
  const [existingCompany, setExistingCompany] = useState<any>(null)

  // Add a state to track if we've already checked the domain
  const [domainChecked, setDomainChecked] = useState(false)

  // Add password state
  const [password, setPassword] = useState('123456Aa!')

  // Team member invitation
  const [memberEmail, setMemberEmail] = useState('')
  const [companyUrl, setCompanyUrl] = useState('')
  const [userRole, setUserRole] = useState('seller')
  const [inviteSent, setInviteSent] = useState(false)
  const [inviteLoading, setInviteLoading] = useState(false)
  const [inviteError, setInviteError] = useState('')
  const [userId, setUserId] = useState('')

  // Update user company
  const [updateSuccess, setUpdateSuccess] = useState(false)
  const [updateLoading, setUpdateLoading] = useState(false)
  const [updateError, setUpdateError] = useState('')

  // Add states for deletion
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [deletingCompany, setDeletingCompany] = useState(false)
  const [deleteError, setDeleteError] = useState('')

  // Prefill company URL when domain changes - without https://
  useEffect(() => {
    if (companyDomain) {
      setCompanyUrl(companyDomain)
    }
  }, [companyDomain, companyCreated])

  // Check if company domain exists
  const checkCompanyDomain = async () => {
    if (!companyDomain) {
      setCompanyError('Please enter a company domain')
      return
    }

    setCheckingDomain(true)
    setCompanyError('')
    setCompanyExists(false)
    setExistingCompany(null)
    setDomainChecked(true) // Mark that we've checked the domain

    try {
      const response = await axios.get(`/ccompanies/${companyDomain}`)

      setExistingCompany(response.data?.data || response.data)
      setCreatedCompanyId(response.data?.data?.id || response.data?.id || '')
      setCompanyExists(true)
      setCompanyCreated(true)
      setCompanyUrl(companyDomain)
      setCheckingDomain(false)
    } catch (error: any) {
      // If 404, company doesn't exist which is fine
      if (error.response?.status === 404) {
        setCompanyExists(false)
        setCheckingDomain(false)
      } else {
        setCompanyError(
          error.response?.data?.message || 'Error checking company domain',
        )
        setCheckingDomain(false)
      }
    }
  }

  const createCompany = async (e: React.FormEvent) => {
    e.preventDefault()

    // First check if the company exists to prevent duplicates
    setCheckingDomain(true)

    try {
      // Try to get the company first
      const checkResponse = await axios.get(`/ccompanies/${companyDomain}`)

      // If we reach here, company exists
      setExistingCompany(checkResponse.data?.data || checkResponse.data)
      setCreatedCompanyId(
        checkResponse.data?.data?.id || checkResponse.data?.id || '',
      )
      setCompanyExists(true)
      setCompanyCreated(true)
      setCompanyUrl(companyDomain)
      setCheckingDomain(false)

      return // Exit early since company already exists
    } catch (error: any) {
      // If 404, company doesn't exist which is what we want
      if (error.response?.status !== 404) {
        setCompanyError(
          error.response?.data?.message || 'Error checking company domain',
        )
        setCheckingDomain(false)
        return // Exit on unexpected error
      }
    }

    // Continue to create the company since it doesn't exist
    setCheckingDomain(false)
    setCompanyLoading(true)
    setCompanyError('')

    try {
      const response = await axios.post(`/ccompanies`, {
        domain: companyDomain,
        status: 'approved',
      })

      setCreatedCompanyId(response.data.id || response.data.data?.id || '')
      setCompanyCreated(true)
      setCompanyLoading(false)
      // Update the company URL without https://
      setCompanyUrl(companyDomain)
    } catch (error: any) {
      setCompanyError(error.response?.data?.message || 'Error creating company')
      setCompanyLoading(false)
    }
  }

  // Handle Enter key on company domain input
  const handleCompanyDomainKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !companyCreated && !checkingDomain) {
      e.preventDefault()
      checkCompanyDomain()
    }
  }

  const inviteTeamMember = async (e: React.FormEvent) => {
    e.preventDefault()
    setInviteLoading(true)
    setInviteError('')

    try {
      const response = await axios.post(`/invite-team-member`, {
        username: memberEmail,
        company_url: companyUrl,
        role: userRole,
        is_verified: true,
        password: password,
        autoVerify: true,
      })

      // Store user ID if returned in the response for later use
      if (response.data?.id) {
        setUserId(response.data.id)
      } else if (response.data?.data?.id) {
        setUserId(response.data.data.id)
      }

      setInviteSent(true)
      setInviteLoading(false)
    } catch (error: any) {
      setInviteError(
        error.response?.data?.message || 'Error inviting team member',
      )
      setInviteLoading(false)
    }
  }

  const handleUpdateUserCompany = async (e: React.FormEvent) => {
    e.preventDefault()
    setUpdateLoading(true)
    setUpdateError('')

    if (!userId) {
      setUpdateError('User ID is required')
      setUpdateLoading(false)
      return
    }

    if (!createdCompanyId) {
      setUpdateError('Company ID is required')
      setUpdateLoading(false)
      return
    }

    if (!companyUrl) {
      setUpdateError('Company URL is required')
      setUpdateLoading(false)
      return
    }

    try {
      // Use the correct endpoint as requested
      await axios.post(`/users/${userId}/migrate-company`, {
        company_url: companyUrl,
        companyId: createdCompanyId,
      })

      setUpdateSuccess(true)
      setUpdateLoading(false)
    } catch (error: any) {
      setUpdateError(
        error.response?.data?.message || 'Error updating user company',
      )
      setUpdateLoading(false)
    }
  }

  const resetAll = () => {
    // Reset all states
    setCompanyDomain('')
    setCompanyCreated(false)
    setCreatedCompanyId('')
    setCompanyError('')
    setCompanyExists(false)
    setExistingCompany(null)
    setDomainChecked(false) // Reset the domain checked state

    setMemberEmail('')
    setCompanyUrl('')
    setUserRole('seller')
    setInviteSent(false)
    setInviteError('')
    setUserId('')

    setUpdateSuccess(false)
    setUpdateError('')
  }

  // Add delete company function
  const deleteCompany = async () => {
    if (!companyDomain) return

    setDeletingCompany(true)
    setDeleteError('')

    try {
      await axios.delete(`/ccompanies/${companyDomain}`)

      // Reset form on successful deletion
      resetAll()
      setDeletingCompany(false)
      setShowDeleteConfirmation(false)
    } catch (error: any) {
      setDeleteError(error.response?.data?.message || 'Error deleting company')
      setDeletingCompany(false)
    }
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
        }}
      >
        <Typography variant="h4" component="h1">
          Create New Company and User
        </Typography>
        <Button variant="outlined" onClick={onBack}>
          Back to Dashboard
        </Button>
      </Box>

      {/* Step 1: Check Company Domain */}
      <FormContainer elevation={3}>
        <Typography variant="h6" gutterBottom>
          Step 1: Check Company Domain
        </Typography>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            checkCompanyDomain()
          }}
        >
          <FormField>
            <TextField
              label="Company Domain"
              fullWidth
              value={companyDomain}
              onChange={(e) => setCompanyDomain(e.target.value)}
              onKeyDown={handleCompanyDomainKeyDown}
              required
              disabled={companyCreated}
              helperText="Domain without http:// (e.g. example.com)"
            />
          </FormField>

          {companyError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {companyError}
            </Alert>
          )}

          {!companyCreated && (
            <Button
              type="submit"
              variant="contained"
              disabled={checkingDomain || !companyDomain}
              sx={{ mr: 2 }}
            >
              {checkingDomain ? <CircularProgress size={24} /> : 'Check Domain'}
            </Button>
          )}

          {companyExists && (
            <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <div>
                  Company already exists! Using existing company ID:{' '}
                  {createdCompanyId}
                </div>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => setShowDeleteConfirmation(true)}
                  sx={{ ml: 2 }}
                >
                  Delete
                </Button>
              </Box>
            </Alert>
          )}

          {showDeleteConfirmation && (
            <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to delete company "{companyDomain}"?
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  onClick={deleteCompany}
                  disabled={deletingCompany}
                >
                  {deletingCompany ? (
                    <CircularProgress size={24} />
                  ) : (
                    'Yes, Delete'
                  )}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setShowDeleteConfirmation(false)}
                >
                  Cancel
                </Button>
              </Box>
            </Alert>
          )}

          {deleteError && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {deleteError}
            </Alert>
          )}

          {!companyExists &&
            !companyCreated &&
            domainChecked &&
            !checkingDomain && (
              <>
                <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                  Company doesn't exist yet. You can create it.
                </Alert>

                <Button
                  type="button"
                  variant="contained"
                  onClick={createCompany}
                  disabled={companyCreated || companyLoading || !companyDomain}
                >
                  {companyLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    'Create Company'
                  )}
                </Button>
              </>
            )}

          {companyCreated && !companyExists && (
            <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
              Company created successfully! ID: {createdCompanyId}
            </Alert>
          )}
        </form>
      </FormContainer>

      {/* Step 2: Invite Team Member */}
      <FormContainer elevation={3}>
        <Typography variant="h6" gutterBottom>
          Step 2: Invite Team Member
        </Typography>
        <form onSubmit={inviteTeamMember}>
          <FormField>
            <TextField
              label="Email"
              fullWidth
              value={memberEmail}
              onChange={(e) => setMemberEmail(e.target.value)}
              required
              disabled={inviteSent}
              type="email"
            />
          </FormField>
          <FormField>
            <TextField
              label="Company URL"
              fullWidth
              value={companyUrl}
              InputProps={{
                readOnly: true,
              }}
              disabled
              helperText="Domain from step 1 (read-only)"
            />
          </FormField>
          <FormField>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={userRole}
                label="Role"
                onChange={(e) => setUserRole(e.target.value)}
                disabled={inviteSent}
              >
                <MenuItem value="seller">Seller</MenuItem>
                <MenuItem value="buyer">Buyer</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
          </FormField>
          <FormField>
            <TextField
              label="Password"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={inviteSent}
              type="text"
              helperText="Leave blank to generate a random password"
            />
          </FormField>
          {inviteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {inviteError}
            </Alert>
          )}
          {inviteSent && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Invitation sent successfully!
              {userId && (
                <Box sx={{ mt: 1, fontWeight: 'bold' }}>
                  Created User ID: {userId}
                </Box>
              )}
            </Alert>
          )}
          <Button
            type="submit"
            variant="contained"
            disabled={
              inviteSent || inviteLoading || !memberEmail || !companyUrl
            }
          >
            {inviteLoading ? (
              <CircularProgress size={24} />
            ) : (
              'Invite Team Member'
            )}
          </Button>
        </form>
      </FormContainer>

      {/* Step 3: Update User Company */}
      <FormContainer elevation={3}>
        <Typography variant="h6" gutterBottom>
          Step 3: Update User Company
        </Typography>
        <form onSubmit={handleUpdateUserCompany}>
          <FormField>
            <TextField
              label="User ID"
              fullWidth
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              helperText="User ID"
              sx={{ mb: 2 }}
            />
          </FormField>
          <FormField>
            <TextField
              label="Company ID"
              fullWidth
              value={createdCompanyId}
              InputProps={{
                readOnly: true,
              }}
              disabled
              helperText="Company ID from step 1 (read-only)"
              sx={{ mb: 2 }}
            />
          </FormField>
          <FormField>
            <TextField
              label="Company URL"
              fullWidth
              value={companyUrl}
              InputProps={{
                readOnly: true,
              }}
              disabled
              helperText="Domain from step 1 (read-only)"
            />
          </FormField>
          {updateError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {updateError}
            </Alert>
          )}
          {updateSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              User company updated successfully!
            </Alert>
          )}
          <Button
            type="submit"
            variant="contained"
            disabled={
              updateSuccess ||
              updateLoading ||
              !userId ||
              !createdCompanyId ||
              !companyUrl
            }
          >
            {updateLoading ? (
              <CircularProgress size={24} />
            ) : (
              'Update User Company'
            )}
          </Button>
        </form>
      </FormContainer>

      {/* Reset Button */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Button variant="outlined" onClick={resetAll}>
          Reset All Forms
        </Button>
      </Box>
    </>
  )
}

// Main Admin Console Component
const AdminConsole = () => {
  // State to manage which admin component to display
  const [activePage, setActivePage] = useState<string | null>(null)

  // Function to navigate back to the dashboard
  const navigateToDashboard = () => {
    setActivePage(null)
  }

  // Render active page based on selection
  const renderActivePage = () => {
    switch (activePage) {
      case 'company-user-creation':
        return <CompanyUserCreationForm onBack={navigateToDashboard} />
      // Add more cases for other admin pages
      default:
        return null
    }
  }

  // Admin Dashboard
  const renderDashboard = () => (
    <AdminDashboard>
      <Typography variant="h4" component="h1" gutterBottom>
        Admin Console
      </Typography>

      <Typography variant="body1" paragraph>
        Welcome to the Admin Console. Select an operation to perform:
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6} sx={{ mx: 'auto' }}>
          <AdminActionCard>
            <CardContent>
              <Typography variant="h6" component="h2" gutterBottom>
                Company & User Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create a new company and users belonging to it (even if email
                domain does not match).
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                variant="contained"
                fullWidth
                onClick={() => setActivePage('company-user-creation')}
              >
                Create New Company and User
              </Button>
            </CardActions>
          </AdminActionCard>
        </Grid>
      </Grid>
    </AdminDashboard>
  )

  return (
    <Container>
      <AdminContainer>
        {activePage ? renderActivePage() : renderDashboard()}
      </AdminContainer>
    </Container>
  )
}

export default AdminConsole
